/**
 * Scene Hierarchy Zustand store — INIT-2026-Q3-SCENE-HIERARCHY, Phase C + WP-B.
 *
 * Separate from `useViewerStore` per DEC-91-01 (avoid breaking the
 * RFC-108.1 SDK drift test) and DEC-91-02 (feature-flag gating).
 *
 * The `featureFlagEnabled` field is the kill-switch for Scene Tree V2:
 *   - `false` (default) → existing `SceneTreePanel` renders.
 *   - `true`            → `SceneTreeV2` renders instead.
 *
 * WP-B addition: `alignedToEdges` accumulates `AlignedToEdgeRecord` entries
 * written by `SceneTreeDropArea` when a CRS decision is persisted.
 * See LAYER_LIFECYCLE_AND_PORTABLE_LAYER_AUDIT.md §9.6.
 *
 * Usage:
 * ```tsx
 * const { manifest, selectedNodeId, featureFlagEnabled } =
 *   useSceneHierarchyStore();
 * ```
 */

import { create } from "zustand";
import type {
  ControlOverlay,
  ControlOverlayEntry,
  SceneHierarchyManifest,
} from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";
import type { SceneItem } from "@/sdk-stub/gtwin-sdk/types";
import type { AdapterError, AdapterResult, DomainAdapterContext } from "@/components/scene-tree/adapters/DomainAdapter";
import { loadTier2Manifest } from "@/components/scene-tree/manifestLoader";
import { fetchObjectIndexManifest } from "@/components/scene-tree/fetchObjectIndexManifest";

// ─────────────────────────────────────────────────────────────────────────────
// AlignedToEdge record — §9.6 verbatim
// ─────────────────────────────────────────────────────────────────────────────

/**
 * An immutable audit record written into the Scene Hierarchy index when a
 * CRS decision is persisted. Models the `alignedTo` edge in the Object Index.
 *
 * All fields are verbatim from LAYER_LIFECYCLE_AND_PORTABLE_LAYER_AUDIT.md §9.6.
 */
export interface AlignedToEdgeRecord {
  /** ULID uniquely identifying this edge. */
  readonly id: string;
  /** The layer's tree node id (or sceneItemId) — the `from` vertex. */
  readonly fromNodeId: string;
  /** Target anchor node — typically `"node:project-anchor"`. */
  readonly toNodeId: string;
  /** How the alignment was computed and persisted. */
  readonly method:
    | "viewer-matrix"
    | "converter-reproject"
    | "anchor-promotion"
    | "reproject-on-read";
  /** Source CRS metadata. */
  readonly source: { crs: string };
  /** Target CRS metadata. */
  readonly target: { crs: string };
  /**
   * 4×4 column-major transformation matrix (flat 16-element array).
   * Present only when `method === "viewer-matrix"`.
   */
  readonly matrix?: readonly number[];
  /**
   * Scale mode for viewer-matrix alignment.
   * Present only when `method === "viewer-matrix"`.
   */
  readonly scaleMode?: "visual" | "engineering";
  /** ISO-8601 timestamp of the decision. */
  readonly decisionAt: string;
  /** Identity of the deciding user. */
  readonly decisionBy: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// State shape
// ─────────────────────────────────────────────────────────────────────────────

export interface SceneHierarchyState {
  /** The active Tier-2 manifest, or null when no asset is drilled into. */
  manifest: SceneHierarchyManifest | null;

  /** Currently selected node id, or null when nothing is selected. */
  selectedNodeId: string | null;

  /** True while the manifest is being fetched from the server. */
  isLoading: boolean;

  /**
   * Feature flag for Scene Tree V2. Default `false` (off).
   * Set to `true` to activate the new virtualised tree.
   * Per DEC-91-02: rollback = set to `false`.
   */
  featureFlagEnabled: boolean;

  /**
   * Dev/QA only — a benchmark manifest (e.g. the Synthetic Hierarchy
   * Benchmark) that, when set, OVERRIDES the manifest the SceneTreeV2
   * container would build from the live tree. Lets a tester load the
   * SHB into the running viewer to verify the spatial/functional aspect
   * toggle and ISO 81346 designations on deterministic data. Null in
   * normal operation.
   */
  benchmarkManifest: SceneHierarchyManifest | null;

  /**
   * Manifest fetched from a `SceneItem.hierarchyUrl` (INIT-2026-Q3 close-out C2).
   * Large object indexes are delivered by reference, not inline; when the gTwin
   * payload carries a `hierarchyUrl` but no inline `objectIndex`, the container
   * fetches the index and stores the converted manifest here. Null otherwise.
   */
  urlObjectIndexManifest: SceneHierarchyManifest | null;

  /**
   * The `hierarchyUrl` that `urlObjectIndexManifest` was loaded from. Used to
   * avoid re-fetching the same URL and to detect when the source changed.
   */
  urlObjectIndexSource: string | null;

  /**
   * Last Tier-2 load error, or null when no error has occurred.
   * Set by `loadTier2For` on failure; cleared on successful load.
   */
  tier2Error: AdapterError | null;

  /**
   * Append-only log of `alignedTo` edges recorded by `SceneTreeDropArea`
   * when a CRS decision is persisted. See §9.6 of the audit.
   *
   * Initialises to an empty tuple and grows with each `recordAlignedToEdge`
   * call. Entries are never removed within a session (audit immutability).
   */
  alignedToEdges: readonly AlignedToEdgeRecord[];

  // ── Actions ──────────────────────────────────────────────────────────────

  /** Replace the active manifest (pass `null` to clear / telescope out). */
  setManifest: (manifest: SceneHierarchyManifest | null) => void;

  /** Set the selected node id (pass `null` to clear selection). */
  selectNode: (nodeId: string | null) => void;

  /** Set the loading state while a manifest fetch is in flight. */
  setLoading: (loading: boolean) => void;

  /** Enable or disable Scene Tree V2 at runtime. */
  setFeatureFlag: (enabled: boolean) => void;

  /** Dev/QA — set or clear the benchmark manifest override. */
  setBenchmarkManifest: (manifest: SceneHierarchyManifest | null) => void;

  /**
   * Fetch an `object-index.json` from `url` (a SceneItem.hierarchyUrl) and, on
   * success, store the converted manifest in `urlObjectIndexManifest`. Idempotent
   * per URL: a no-op if `url` is already the loaded source. Never throws.
   *
   * @returns `true` if a manifest was loaded (or already present), else `false`.
   */
  loadObjectIndexFromUrl: (url: string) => Promise<boolean>;

  /** Clear the URL-loaded object-index manifest (e.g. on scene unload). */
  clearUrlObjectIndex: () => void;

  /**
   * Appends an `AlignedToEdgeRecord` to `alignedToEdges`.
   *
   * Called by `SceneTreeDropArea` after the CRS wizard emits a decision.
   * Per §9.6: the edge is immutable once written; callers must not mutate
   * the record after passing it here.
   *
   * @param edge - The fully-populated edge record to append.
   */
  recordAlignedToEdge: (edge: AlignedToEdgeRecord) => void;

  /**
   * Session-level visualization control overlay (Block 1, decision #4).
   * Keyed by objectId; lives in viewer session only — never serialized.
   * Source: scene-hierarchy-types §6; 98-adoption-plan.md §Block1.
   */
  controlOverlay: ControlOverlay;

  /**
   * Merge a partial ControlOverlayEntry for `objectId` into the overlay.
   * Creates the entry if it does not yet exist; merges fields if it does.
   * Passing `highlighted: false` explicitly clears a highlight.
   */
  setControlOverlayEntry(objectId: string, entry: Partial<ControlOverlayEntry>): void;

  /**
   * Batch-merge many partial ControlOverlayEntry values in a single state
   * update. Each key is an `objectId`; each value is merged into the existing
   * entry (or creates a new one). Used by Block 2 overlay actions (isolate,
   * color-by-aspect) that touch many objects at once — a single `set` avoids
   * N store notifications. Equivalent to calling `setControlOverlayEntry` for
   * each key but atomic.
   */
  mergeControlOverlay(entries: Record<string, Partial<ControlOverlayEntry>>): void;

  /** Remove all entries from the ControlOverlay (e.g. on scene unload). */
  clearControlOverlay(): void;

  /**
   * Merge per-object `temporalState` values into the ControlOverlay (C6/G6).
   * Driven by the 4D timeline: each key is an `objectId`, each value is the
   * evaluated temporal state for the current scrub time. Existing overlay
   * fields (visibility, color, etc.) are preserved.
   */
  setTemporalStates(states: Record<string, unknown>): void;

  /**
   * Loads the Tier-2 manifest for `item` and updates the store.
   *
   * - Sets `isLoading: true` before the adapter call.
   * - On success: sets `manifest` to the result and clears `tier2Error`.
   * - On failure: leaves `manifest` unchanged and sets `tier2Error`.
   * - Always sets `isLoading: false` on completion.
   *
   * @param item    - The SceneItem to build the manifest for.
   * @param asset   - Identity triple for the manifest root.
   * @param context - Optional IO dependencies (fetchJson, signal).
   * @returns The raw `AdapterResult` for callers that need it.
   */
  loadTier2For: (
    item: SceneItem,
    asset: { layerId: string; assetNodeId: string; gtwinId: string },
    context?: DomainAdapterContext,
  ) => Promise<AdapterResult>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolve the initial Scene Tree V2 feature-flag value (G2/C8 cutover).
 *
 * Policy:
 *   - `VITE_SCENE_TREE_V2=on|true`  → forced ON (any env).
 *   - `VITE_SCENE_TREE_V2=off|false`→ forced OFF (any env).
 *   - otherwise: ON in dev/UAT (`import.meta.env.DEV`), OFF in production.
 *
 * The test runner (`MODE === "test"`) is always OFF so unit tests and the
 * legacy-fallback paths stay deterministic. Runtime rollback is unchanged:
 * `setFeatureFlag(false)` (DEC-91-02).
 */
function resolveInitialFeatureFlag(): boolean {
  const env = (typeof import.meta !== "undefined" ? import.meta.env : undefined) as
    | { DEV?: boolean; MODE?: string; VITE_SCENE_TREE_V2?: string }
    | undefined;
  const explicit = env?.VITE_SCENE_TREE_V2;
  if (explicit === "on" || explicit === "true") return true;
  if (explicit === "off" || explicit === "false") return false;
  return Boolean(env?.DEV) && env?.MODE !== "test";
}

export const useSceneHierarchyStore = create<SceneHierarchyState>((set) => ({
  manifest: null,
  selectedNodeId: null,
  isLoading: false,
  featureFlagEnabled: resolveInitialFeatureFlag(),
  benchmarkManifest: null,
  urlObjectIndexManifest: null,
  urlObjectIndexSource: null,
  tier2Error: null,
  alignedToEdges: [],
  controlOverlay: { byObject: {} },

  setManifest: (manifest) => set({ manifest }),

  selectNode: (nodeId) => set({ selectedNodeId: nodeId }),

  setLoading: (loading) => set({ isLoading: loading }),

  setFeatureFlag: (enabled) => set({ featureFlagEnabled: enabled }),

  setBenchmarkManifest: (manifest) => set({ benchmarkManifest: manifest }),

  loadObjectIndexFromUrl: async (url) => {
    if (!url) return false;
    const state = useSceneHierarchyStore.getState();
    if (state.urlObjectIndexSource === url && state.urlObjectIndexManifest) {
      return true; // already loaded this URL
    }
    set({ isLoading: true });
    const manifest = await fetchObjectIndexManifest(url);
    if (manifest) {
      set({
        urlObjectIndexManifest: manifest,
        urlObjectIndexSource: url,
        isLoading: false,
      });
      return true;
    }
    set({ isLoading: false });
    return false;
  },

  clearUrlObjectIndex: () =>
    set({ urlObjectIndexManifest: null, urlObjectIndexSource: null }),

  recordAlignedToEdge: (edge) =>
    set((state) => ({
      alignedToEdges: [...state.alignedToEdges, edge],
    })),

  setControlOverlayEntry: (objectId, entry) =>
    set((state) => ({
      controlOverlay: {
        byObject: {
          ...state.controlOverlay.byObject,
          [objectId]: {
            ...state.controlOverlay.byObject[objectId],
            ...entry,
          },
        },
      },
    })),

  mergeControlOverlay: (entries) =>
    set((state) => {
      const byObject = { ...state.controlOverlay.byObject };
      for (const [objectId, entry] of Object.entries(entries)) {
        byObject[objectId] = { ...byObject[objectId], ...entry };
      }
      return { controlOverlay: { byObject } };
    }),

  clearControlOverlay: () => set({ controlOverlay: { byObject: {} } }),

  setTemporalStates: (states) =>
    set((state) => {
      const keys = Object.keys(states);
      if (keys.length === 0) return state;
      const byObject = { ...state.controlOverlay.byObject };
      for (const objectId of keys) {
        byObject[objectId] = {
          ...byObject[objectId],
          temporalState: states[objectId],
        };
      }
      return { controlOverlay: { byObject } };
    }),

  loadTier2For: async (item, asset, context = {}) => {
    set({ isLoading: true });
    const result = await loadTier2Manifest(item, asset, context);
    if (result.ok) {
      set({ manifest: result.manifest, tier2Error: null, isLoading: false });
    } else {
      set({ tier2Error: result.error, isLoading: false });
    }
    return result;
  },
}));

// ─────────────────────────────────────────────────────────────────────────────
// Control-overlay selectors / hooks (Block 2 — render the overlay)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Selector — returns the {@link ControlOverlayEntry} for `objectId`, or
 * `undefined` when no entry exists. Pure; safe to call outside React.
 */
export function selectControlOverlayEntry(
  state: SceneHierarchyState,
  objectId: string,
): ControlOverlayEntry | undefined {
  return state.controlOverlay.byObject[objectId];
}

/**
 * React hook — subscribes to the {@link ControlOverlayEntry} for a single
 * `objectId`. Re-renders only when that object's entry reference changes
 * (zustand reference equality on the per-object slice). Returns `undefined`
 * when the object has no overlay entry.
 *
 * Block 2, Task A.1 — the read-side counterpart of `setControlOverlayEntry`.
 */
export function useControlOverlayEntry(
  objectId: string | null | undefined,
): ControlOverlayEntry | undefined {
  return useSceneHierarchyStore((s) =>
    objectId ? s.controlOverlay.byObject[objectId] : undefined,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Dev-mode debug helper
// ─────────────────────────────────────────────────────────────────────────────
// Exposes the store on `window.__sceneHierarchy` ONLY when Vite is in
// development mode (`import.meta.env.DEV`). Production bundles strip the
// helper because the entire block is dead-code-eliminated by the bundler
// when DEV is false. Use from the browser console to flip the feature
// flag without HMR-reloading:
//
//   window.__sceneHierarchy.setFeatureFlag(true)
//   window.__sceneHierarchy.getState()
//
// Per DEC-91-02 the runtime feature flag is the single source of truth
// for Scene Tree V2 visibility; this helper changes nothing about the
// production behaviour.
if (import.meta.env.DEV && typeof window !== "undefined") {
  type DevHelper = {
    getState: typeof useSceneHierarchyStore.getState;
    setFeatureFlag: SceneHierarchyState["setFeatureFlag"];
    selectNode: SceneHierarchyState["selectNode"];
    enable: () => void;
    disable: () => void;
    loadBenchmark: () => Promise<void>;
    clearBenchmark: () => void;
    loadObjectIndex: (urlOrPath: string) => Promise<void>;
  };
  const helper: DevHelper = {
    getState: useSceneHierarchyStore.getState,
    setFeatureFlag: (enabled) =>
      useSceneHierarchyStore.getState().setFeatureFlag(enabled),
    selectNode: (nodeId) =>
      useSceneHierarchyStore.getState().selectNode(nodeId),
    enable: () =>
      useSceneHierarchyStore.getState().setFeatureFlag(true),
    disable: () =>
      useSceneHierarchyStore.getState().setFeatureFlag(false),
    // Loads the Synthetic Hierarchy Benchmark into the live tree so a
    // tester can SEE the spatial/functional aspect toggle and ISO 81346
    // designations on deterministic data. Enables V2 automatically.
    loadBenchmark: async () => {
      const mod = await import(
        "@/__fixtures__/shb/shb-tiny.manifest.json"
      );
      const manifest = (mod.default ?? mod) as unknown as SceneHierarchyManifest;
      const s = useSceneHierarchyStore.getState();
      s.setFeatureFlag(true);
      s.setBenchmarkManifest(manifest);
       
      console.info(
        "[scene-hierarchy] SHB benchmark loaded:",
        manifest.totalNodes,
        "nodes. Use the Spatial/Functional toggle to see aspect re-grouping.",
      );
    },
    clearBenchmark: () =>
      useSceneHierarchyStore.getState().setBenchmarkManifest(null),
    /**
     * Fetch a converter-produced `object-index.json`, convert it to a
     * SceneHierarchyManifest, enable Scene Tree V2, and mount it as the
     * benchmark override so the real model renders in the Scene Tree with the
     * Spatial/Functional aspect toggle available.
     *
     * @example
     *   await window.__sceneHierarchy.loadObjectIndex('/offshore.object-index.json')
     */
    loadObjectIndex: async (urlOrPath: string) => {
      let raw: unknown;
      try {
        const response = await fetch(urlOrPath);
        if (!response.ok) {
           
          console.error(
            `[scene-hierarchy] loadObjectIndex: fetch failed for "${urlOrPath}" — HTTP ${response.status} ${response.statusText}`,
          );
          return;
        }
        raw = await response.json();
      } catch (err) {
         
        console.error(
          `[scene-hierarchy] loadObjectIndex: failed to fetch/parse "${urlOrPath}"`,
          err,
        );
        return;
      }

      let manifest: SceneHierarchyManifest;
      try {
        const { objectIndexToManifest } = await import(
          "@/components/scene-tree/objectIndexToManifest"
        );
         
        manifest = objectIndexToManifest(raw as any);
      } catch (err) {
         
        console.error(
          `[scene-hierarchy] loadObjectIndex: conversion failed for "${urlOrPath}"`,
          err,
        );
        return;
      }

      const s = useSceneHierarchyStore.getState();
      s.setFeatureFlag(true);
      s.setBenchmarkManifest(manifest);
       
      console.info(
        `[scene-hierarchy] Object index loaded: ${manifest.totalNodes} nodes`,
        `(gtwinId: ${manifest.gtwinId}, domain: ${manifest.domain}).`,
        "Use the Spatial/Functional toggle in Scene Tree V2 to explore aspect re-grouping.",
      );
    },
  };
  (window as unknown as { __sceneHierarchy: DevHelper }).__sceneHierarchy =
    helper;
}
